import { EmployeeBasicInfo } from '../employees/employees.model';
import { Supplier } from '../purchased-order/purchased-order.model';

export enum RawMaterialCategory {
  CHEMICALS = 'chemicals',
  TECHNICALS = 'technicals',
  BOTTLES = 'bottles',
  STICKERS = 'stickers',
  PACKAGING_MATERIALS = 'packaging-materials',
}

export interface RawMaterialCategoryItem {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  short_code: string;
}

export interface RawMaterialItem {
  id: string;
  name: string;
  code: string;
  category_name: RawMaterialCategory;
  category: string;
  category_short_code: string;
  created_at: string;
  updated_at: string;
  unit: MeasurementUnit;
  min_stock_level: number;
  gst_percentage: string | number | null; // default GST rate, "0.00" means exempt
  company: string | null; // UUID
  company_name: string | null;
  hsn_code: string | null;
}

export interface RawMaterialWarehouse {
  id: string;
  name: string;
  address: string;
  created_at: string;
  updated_at: string;
  plant_head: EmployeeBasicInfo | null;
}

export enum RawMaterialTab {
  OVERVIEW = 'overview',
  CATEGORIES = 'categories',
  MATERIAL = 'material',
  INVENTORY = 'inventory',
  ENTRIES = 'entries',
  PURCHASED_ORDER = 'purchased-order',
  SUPPLIERS = 'suppliers',
  STOCK_ADJUSTMENTS = 'stock-adjustments',
}

export interface RawMaterialRequest {
  name: string;
  code?: string;
  category: string;
  unit: string;
  min_stock_level: number;
  gst_percentage?: number | null;
  company?: string | null;
  hsn_code?: string | null;
}

export interface MeasurementUnit {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  short_name: string;
}

export enum QualityCheckStatus {
  PASSED = 'passed',
  FAILED = 'failed',
}

export interface RawMaterialStock {
  id: string;
  raw_material_name: string;
  raw_material_code: string;
  raw_material_category_name: string;
  raw_material_category_short_code: string;
  warehouse_name: string;
  unit_name: string;
  unit_short_name: string;
  min_stock_level: number;
  is_low_stock: boolean;
  created_at: string; // ISO date-time
  updated_at: string; // ISO date-time
  units_available: string;
  raw_material: string; // raw material ID (UUID)
  warehouse: string; // warehouse ID (UUID)
  unit: string; // unit ID (UUID)
}

export enum StockAdjustmentReason {
  STOCK_TAKE = 'stock_take',
  MISSING_PURCHASE_ORDER = 'missing_purchase_order',
  DAMAGE_OR_EXPIRY = 'damage_or_expiry',
  RETURN_TO_SUPPLIER = 'return_to_supplier',
  CORRECTION = 'correction',
  OTHER = 'other',
}

/**
 * Our own labels. The API's `reason_display` is title-cased straight from the code
 * ("Damage Or Expiry"), so prefer these everywhere a reason is shown to a user.
 */
export const STOCK_ADJUSTMENT_REASON_LABELS: Record<StockAdjustmentReason, string> = {
  [StockAdjustmentReason.STOCK_TAKE]: 'Physical stock count',
  [StockAdjustmentReason.MISSING_PURCHASE_ORDER]: 'Received without a PO',
  [StockAdjustmentReason.DAMAGE_OR_EXPIRY]: 'Damaged or expired',
  [StockAdjustmentReason.RETURN_TO_SUPPLIER]: 'Returned to supplier',
  [StockAdjustmentReason.CORRECTION]: 'Data correction',
  [StockAdjustmentReason.OTHER]: 'Other (remarks required)',
};

export interface StockAdjustmentReasonOption {
  value: StockAdjustmentReason;
  label: string;
}

export const STOCK_ADJUSTMENT_REASON_OPTIONS: StockAdjustmentReasonOption[] = (
  Object.keys(STOCK_ADJUSTMENT_REASON_LABELS) as StockAdjustmentReason[]
).map((value: StockAdjustmentReason) => ({ value, label: STOCK_ADJUSTMENT_REASON_LABELS[value] }));

export enum StockAdjustmentDirection {
  ADD = 'add',
  REMOVE = 'remove',
}

/** Every decimal below arrives from DRF as a string. Parse before comparing or adding. */
export interface StockAdjustment {
  id: string;
  raw_material: string;
  raw_material_name: string;
  raw_material_code: string;
  warehouse: string;
  warehouse_name: string;
  unit_short_name: string;
  quantity: string; // signed: positive adds stock, negative removes it
  reason: StockAdjustmentReason;
  reason_display: string;
  remarks: string | null;
  units_available_after: string; // balance immediately after this adjustment
  created_by: string;
  created_by_name: string;
  created_at: string;
}

export interface StockAdjustmentRequest {
  raw_material: string;
  warehouse: string;
  quantity: string; // signed, sent as a string
  reason: StockAdjustmentReason;
  remarks?: string;
}

export interface StockAdjustmentFilters {
  raw_material?: string | null;
  warehouse?: string | null;
  category?: string | null;
  reason?: StockAdjustmentReason | null;
  created_by?: string | null;
  direction?: StockAdjustmentDirection | null;
  from_date?: string | null;
  to_date?: string | null;
  search?: string | null;
  page?: number | null;
  ordering?: string | null;
}

/**
 * The 400 body returned when a removal exceeds what is on the shelf. The numbers are
 * the point of it: show them rather than collapsing the response into a generic error.
 */
export interface StockShortageError {
  quantity: string;
  raw_material: string;
  warehouse: string;
  available: string;
  requested: string;
  shortage: string;
}

/** What the adjustment form needs to know about the row it was opened from. */
export interface StockAdjustmentContext {
  raw_material: string;
  raw_material_name: string;
  raw_material_code: string;
  warehouse: string;
  warehouse_name: string;
  unit_short_name: string;
  units_available?: string | null; // omitted when the current balance is not to hand
}

export interface RawMaterialEntry {
  id: string;
  raw_material_name: string;
  raw_material_code: string;
  unit_name: string;
  unit_short_name: string;
  po_number: string;
  purchase: string;
  warehouse: RawMaterialWarehouse;
  created_at: string;
  updated_at: string;
  batch_number: string | null;
  quantity_received: number;
  date_received: string;
  quality_check_status: string;
  quality_check_remarks: string;
  supplier_info: Supplier;
}
